// The staff apps' route error page (Frontend §3.5 "[New] Any error while a route loads"; UserFlows §4.2, decided
// 2026-10-10): the inline error card instead of TanStack Router's raw default page.
import { isApiError, type ApiErrorCode } from '@pa/api'
import { useRouter, type ErrorComponentProps } from '@tanstack/react-router'
import { AlertTriangle } from 'lucide-react'
import { SOMETHING_WENT_WRONG } from '../errors'

export const ROUTE_ERROR_MESSAGE = SOMETHING_WENT_WRONG

/**
 * Codes the session already handles with a redirect or a sign-out (Frontend §3.5: A-01, A-04, S-04, "no access"):
 * the page shows nothing while that happens, never the card.
 */
const HANDLED_ELSEWHERE: ReadonlySet<ApiErrorCode> = new Set<ApiErrorCode>([
  'unauthorized',
  'password_change_required',
  'impersonation_ended',
  'clinic_inactive',
  'no_access',
])

/**
 * `errorComponent` for both staff apps (root, `_authed`, and the router default, so a page's error renders inside
 * the shell when the shell loaded): "Something went wrong. Try again.", **Retry** (re-runs the route's loaders:
 * `router.invalidate()` after the boundary's `reset()`), and "Ref: {requestId}" when the error carries one. Never the
 * raw message or code.
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter()
  if (isApiError(error) && HANDLED_ELSEWHERE.has(error.code)) return null
  const ref = isApiError(error) ? error.requestId : undefined
  return (
    <div className="route-error">
      <div className="card card-pad route-error-card" role="alert">
        <AlertTriangle aria-hidden />
        <p>{ROUTE_ERROR_MESSAGE}</p>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => {
            reset()
            void router.invalidate()
          }}
        >
          Retry
        </button>
        {ref && <p className="route-error-ref">Ref: {ref}</p>}
      </div>
    </div>
  )
}
